import {
  DfyAddOns,
  DfyOrderDetailsDto,
  DfyOrderLineItem,
  DfyOrderPaymentDetails,
  DfyOrderType,
  DfyPaymentMethod,
} from "../dfy.ts";
import { DfyCouponDto } from "../coupon.ts";

export const DFY_PACKAGE_CREDITS: Record<DfyOrderType, number> = {
  SMALL: 3900,
  MEDIUM: 4900,
  LARGE: 5900,
};

export const DFY_PACKAGE_IMAGE_COUNTS: Record<DfyOrderType, number> = {
  SMALL: 12,
  MEDIUM: 16,
  LARGE: 20,
};

export const DFY_ADD_ON_CREDITS: Record<keyof DfyAddOns, number> = {
  branding: 900,
  lifestyle: 1900,
  virtualStaging: 1900,
  furnitureReveal: 900,
  export4k: 1900,
  aiLabel: 0,
};

export const DFY_MIN_IMAGE_COUNT = 6;

export const DFY_PACKAGE_LABELS: Record<DfyOrderType, string> = {
  SMALL: "Small",
  MEDIUM: "Medium",
  LARGE: "Large",
};

export const DFY_ADD_ON_LABELS: Record<keyof DfyAddOns, string> = {
  branding: "Branding",
  lifestyle: "Lifestyle",
  virtualStaging: "Virtual staging",
  furnitureReveal: "Furniture reveal",
  export4k: "4K export",
  aiLabel: "AI label",
};

export function calculateDfyOrderLineItems(
  details: DfyOrderDetailsDto,
): DfyOrderLineItem[] {
  const lineItems: DfyOrderLineItem[] = [
    {
      id: "package",
      label: `${DFY_PACKAGE_LABELS[details.type]} package`,
      credits: DFY_PACKAGE_CREDITS[details.type],
    },
  ];

  for (const addOnId of Object.keys(
    DFY_ADD_ON_CREDITS,
  ) as (keyof DfyAddOns)[]) {
    if (details.addOns[addOnId] && DFY_ADD_ON_CREDITS[addOnId] > 0) {
      lineItems.push({
        id: addOnId,
        label: DFY_ADD_ON_LABELS[addOnId],
        credits: DFY_ADD_ON_CREDITS[addOnId],
      });
    }
  }

  return lineItems;
}

export function sumDfyOrderLineItemCredits(
  lineItems: DfyOrderLineItem[],
): number {
  return lineItems.reduce((sum, item) => sum + item.credits, 0);
}

// Stripe's minimum charge is $0.50 and credits map 1:1 to cents.
export const DFY_MIN_STRIPE_CREDITS = 50;

export type DfyPayment = {
  creditsFromBalance: number;
  stripeCredits: number;
};

export function calculateDfyPayment({
  totalCredits,
  creditBalance,
  paymentMethod,
}: {
  totalCredits: number;
  creditBalance: number;
  paymentMethod: DfyPaymentMethod;
}): DfyPayment {
  if (paymentMethod === "STRIPE") {
    return {
      creditsFromBalance: 0,
      stripeCredits:
        totalCredits > 0 ? Math.max(totalCredits, DFY_MIN_STRIPE_CREDITS) : 0,
    };
  }

  if (creditBalance >= totalCredits) {
    return {
      creditsFromBalance: totalCredits,
      stripeCredits: 0,
    };
  }

  const remaining = totalCredits - creditBalance;
  const stripeCredits = Math.max(remaining, DFY_MIN_STRIPE_CREDITS);
  const creditsFromBalance = Math.max(totalCredits - stripeCredits, 0);

  return {
    creditsFromBalance,
    stripeCredits,
  };
}

export function applyDfyCoupon(
  lineItems: DfyOrderLineItem[],
  coupon: DfyCouponDto | null,
): DfyCouponDto | null {
  if (!coupon) {
    return null;
  }

  return {
    code: coupon.code,
    credits: Math.min(coupon.credits, sumDfyOrderLineItemCredits(lineItems)),
  };
}

export function calculateDfyOrderPaymentDetails({
  details,
  creditBalance,
  paymentMethod,
  coupon,
}: {
  details: DfyOrderDetailsDto;
  creditBalance: number;
  paymentMethod: DfyPaymentMethod;
  coupon: DfyCouponDto | null;
}): DfyOrderPaymentDetails {
  const lineItems = calculateDfyOrderLineItems(details);
  const appliedCoupon = applyDfyCoupon(lineItems, coupon);
  const payment = calculateDfyPayment({
    totalCredits:
      sumDfyOrderLineItemCredits(lineItems) - (appliedCoupon?.credits ?? 0),
    creditBalance,
    paymentMethod,
  });

  return {
    paymentMethod,
    lineItems,
    coupon: appliedCoupon,
    ...payment,
  };
}

export function getDfyOrderTotalCredits(
  paymentDetails: DfyOrderPaymentDetails,
): number {
  return (
    sumDfyOrderLineItemCredits(paymentDetails.lineItems) -
    (paymentDetails.coupon?.credits ?? 0)
  );
}
